import React, { useState, useEffect } from "react";
import { Card, CardBody, Row, Col, Button, ListGroup, ListGroupItem } from "reactstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { fetchMappedSteps, saveMappedStep, deleteMappedStep, fetchCandidateWorkflows, fetchMappingByRecruiterId } from "./Axios";
import WorkflowEditor from "./WorkflowEditor";


const MappingEditor = ({ mapping, onClose, onWorkflowUpdate = () => {} }) => {

  if (!mapping || !mapping.recruiter_workflow || !mapping.candidate_workflow) {
    return <p className="text-danger">Error: No se puede cargar el mapeo. Faltan datos.</p>;
  }

  const [recruiterSteps, setRecruiterSteps] = useState([]);
  const [candidateSteps, setCandidateSteps] = useState([]);
  const [mappedSteps, setMappedSteps] = useState({});
  const [candidateWorkflows, setCandidateWorkflows] = useState([]);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [lastMappedStep, setLastMappedStep] = useState(null);

  useEffect(() => {
    refreshMapping();
  }, [mapping]);

  useEffect(() => {
    const sortedRecruiterSteps = [...(mapping.recruiter_workflow.steps || [])].sort((a, b) => a.position - b.position);
    const sortedCandidateSteps = [...(mapping.candidate_workflow?.steps || [])].sort((a, b) => a.position - b.position);

    setRecruiterSteps(sortedRecruiterSteps);
    setCandidateSteps(sortedCandidateSteps);
    setMappedSteps({});
    setLastMappedStep(null);

    fetchMappedSteps(mapping.id)
      .then((data) => {
        const groupedMappings = {};
        let lastStep = null;

        data.forEach(({ recruiter_step_id, candidate_step_id }) => {
          if (!groupedMappings[candidate_step_id]) groupedMappings[candidate_step_id] = [];
          groupedMappings[candidate_step_id].push(recruiter_step_id);
        });

        Object.keys(groupedMappings).forEach(candidateStepId => {
          groupedMappings[candidateStepId].sort((a, b) => {
            const stepA = sortedRecruiterSteps.find(s => s.id === a);
            const stepB = sortedRecruiterSteps.find(s => s.id === b);
            return (stepA?.position || 0) - (stepB?.position || 0);
          });
        });

        const remainingSteps = Object.values(groupedMappings).flat();
        if (remainingSteps.length > 0) {
          lastStep = remainingSteps.reduce((last, currentId) => {
            const currentStep = sortedRecruiterSteps.find(s => s.id === currentId);
            const lastStepObj = sortedRecruiterSteps.find(s => s.id === last);
            return (currentStep?.position || 0) > (lastStepObj?.position || 0) ? currentId : last;
          });
        }

        setMappedSteps(groupedMappings);
        setLastMappedStep(lastStep || null);
      });

    fetchCandidateWorkflows().then(setCandidateWorkflows);
  }, [mapping]);

  const refreshMapping = async () => {
    try {
      const updatedMapping = await fetchMappingByRecruiterId(mapping.recruiter_workflow.id);
      if (!updatedMapping) return;

      setRecruiterSteps(updatedMapping.recruiter_workflow.steps.sort((a, b) => a.position - b.position));
      setCandidateSteps(updatedMapping.candidate_workflow.steps.sort((a, b) => a.position - b.position));

      const mappedData = await fetchMappedSteps(updatedMapping.id);
      if (!mappedData) return;

      const groupedMappings = mappedData.reduce((acc, { recruiter_step_id, candidate_step_id }) => {
        acc[candidate_step_id] = acc[candidate_step_id] || [];
        acc[candidate_step_id].push(recruiter_step_id);
        return acc;
      }, {});

      setMappedSteps(groupedMappings);
    } catch {}
  };


  const handleDragEnd = ({ destination, draggableId }) => {
    if (!destination) return;

    const recruiterStepId = parseInt(draggableId, 10);
    const candidateStepId = parseInt(destination.droppableId.replace("candidate-", ""), 10);

    if (!candidateStepId || !recruiterStepId) return;

    const recruiterStep = recruiterSteps.find(step => step.id === recruiterStepId);
    const candidateStep = candidateSteps.find(step => step.id === candidateStepId);

    if (!recruiterStep || !candidateStep) return;

    const recruiterStepPosition = recruiterStep.position;
    const candidateStepPosition = candidateStep.position;
    const maxCandidateStepWithMapping = getMaxCandidateStepWithMapping();

    if (maxCandidateStepWithMapping !== null && recruiterStepPosition !== 1 && candidateStepPosition < maxCandidateStepWithMapping) {
      alert(`No puedes asignar el paso ${recruiterStepPosition} en el paso ${candidateStepPosition}. Debe estar en el paso ${maxCandidateStepWithMapping} o superior.`);
      return;
    }

    if (mappedSteps[candidateStepId]?.includes(recruiterStepId)) return;

    setMappedSteps(prev => ({
      ...prev,
      [candidateStepId]: [...(prev[candidateStepId] || []), recruiterStepId]
    }));

    setLastMappedStep(recruiterStepId);

    saveMappedStep(mapping.id, null, { recruiter_step_id: recruiterStepId, candidate_step_id: candidateStepId })
      .catch(() => {
        setMappedSteps(prev => ({
          ...prev,
          [candidateStepId]: prev[candidateStepId].filter(id => id !== recruiterStepId)
        }));
        setLastMappedStep(null);
      });
  };

  const handleDeleteMapping = async (candidateStepId, recruiterStepId) => {
    try {
      const data = await fetchMappedSteps(mapping.id);
      const mappedStepToDelete = data.find(
        step => step.recruiter_step_id === recruiterStepId && step.candidate_step_id === candidateStepId
      );

      if (!mappedStepToDelete) return;

      setMappedSteps(prev => {
        const updatedMappings = {
          ...prev,
          [candidateStepId]: prev[candidateStepId]?.filter(id => id !== recruiterStepId) || []
        };

        if (!updatedMappings[candidateStepId].length) delete updatedMappings[candidateStepId];

        const remainingSteps = Object.values(updatedMappings).flat();
        const newLastMappedStep = remainingSteps.length
          ? remainingSteps.reduce((last, currentId) => {
            const currentStep = recruiterSteps.find(s => s.id === currentId);
            const lastStep = recruiterSteps.find(s => s.id === last);
            return (currentStep?.position || 0) > (lastStep?.position || 0) ? currentId : last;
          })
          : null;

        setLastMappedStep(newLastMappedStep);
        return updatedMappings;
      });

      await deleteMappedStep(mapping.id, mappedStepToDelete.id);
    } catch {}
  };

  const getMaxCandidateStepWithMapping = () => {
    const candidateStepPositions = Object.keys(mappedSteps)
      .map(id => candidateSteps.find(s => s.id === parseInt(id, 10))?.position)
      .filter(Boolean);

    return candidateStepPositions.length ? Math.max(...candidateStepPositions) : null;
  };

  const isStepDraggable = (step) => {
    if (Object.values(mappedSteps).some(arr => arr.includes(step.id))) return false;
    if (step.position === 1) return true;

    const previousStep = recruiterSteps.find(s => s.position === step.position - 1);
    return previousStep && Object.values(mappedSteps).flat().includes(previousStep.id);
  };

  const resetMapping = async () => {
    try {
      const mappedSteps = await fetchMappedSteps(mapping.id);
      await Promise.all(mappedSteps.map(step => deleteMappedStep(mapping.id, step.id)));

      setMappedSteps({});
      setLastMappedStep(null);
    } catch {}
  };

  if (editingWorkflow) {
    return (
      <WorkflowEditor
        workflow={editingWorkflow}
        onClose={() => {
          setEditingWorkflow(null);
          refreshMapping();
        }}
      />
    );
  }


  return (
    <Card className="mt-4">
      <CardBody>
        <Row className="mb-3">
          <Col md="6" className="text-left">
            <h1 className="fs-4">
              <span className="small d-block">Configurar Mapeo:{" "}</span>
              <span className="fw-bold">{mapping.recruiter_workflow?.name || "Sin Nombre"}</span>
            </h1>
          </Col>
          <Col md="6" className="text-end">
            <Button color="info" onClick={() => setEditingWorkflow(mapping.recruiter_workflow)}>
              {!mapping.locked ? "Editar Workflow del Reclutador" : "ver Workflow del Reclutador" }
            </Button>
            <Button color="info" onClick={() => setEditingWorkflow(mapping.candidate_workflow)}>
              {!mapping.locked ? "Editar Workflow del Candidato" : "ver Workflow del Candidato" }
            </Button>
          </Col>
        </Row>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Row>
            <Col md="4">
              <h5>Steps del Reclutador</h5>
              <Droppable droppableId="recruiter-steps">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="border p-3 rounded bg-light">
                    <ListGroup>
                      {recruiterSteps.map((step, index) => {
                        const isDraggable = isStepDraggable(step);

                        return (
                          <Draggable key={step.id} draggableId={step.id.toString()} index={index} isDragDisabled={!isDraggable}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...(isDraggable ? provided.dragHandleProps : {})}
                                style={{
                                  ...provided.draggableProps.style,
                                  opacity: isDraggable ? 1 : 0.5,
                                  cursor: isDraggable ? "grab" : "not-allowed",
                                  pointerEvents: isDraggable ? "auto" : "none",
                                }}
                              >
                                <ListGroupItem className="mb-2 text-center">
                                  {step.position }- {step.name} {isDraggable ? "" : " 🔒"}
                                </ListGroupItem>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </ListGroup>
                  </div>
                )}
              </Droppable>
            </Col>

            <Col md="8">
              <h5>Steps del Candidato</h5>
              <ListGroup>
                {candidateSteps.map((step) => (
                  <Droppable key={step.id} droppableId={`candidate-${step.id}`}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="mb-3 p-3 border rounded shadow-sm bg-white">
                        <h6 className="text-primary">{step.name}</h6>
                        <ListGroup>
                          {(mappedSteps[step.id] || []).map((recruiterStepId) => {
                            const recruiterStep = recruiterSteps.find(s => s.id === recruiterStepId);
                            return recruiterStep ? (
                              <ListGroupItem key={recruiterStep.id} className="d-flex justify-content-between align-items-center bg-light shadow-sm">
                                {recruiterStep.name}
                                {lastMappedStep === recruiterStep.id && !mapping.locked && (
                                  <Button size="sm" color="danger" onClick={() => handleDeleteMapping(step.id, recruiterStep.id)}>X</Button>
                                )}
                              </ListGroupItem>
                            ) : null;
                          })}
                        </ListGroup>
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                ))}
              </ListGroup>
            </Col>
          </Row>
        </DragDropContext>
        <div className="mt-4 d-flex justify-content-between">
          {!mapping.locked && (
            <Button color="warning" onClick={resetMapping}> Reiniciar Mapeo</Button>
          )}
          <Button color="secondary" onClick={onClose}> Volver a la Lista de Workflows</Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default MappingEditor;
